import { useCallback, useRef, useState } from 'react';
import { faBell } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Snackbar, Stack, Text } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IFormDialogProps } from '~/components/molecules/FormDialog';
import type {
  ISendPushNotificationFormProps,
  ISendPushNotificationFormValues,
} from './SendPushNotificationForm';
import { FormDialog } from '~/components/molecules/FormDialog';
import { SendPushNotificationForm } from './SendPushNotificationForm';

const SNACKBAR_AUTO_HIDE_DURATION = 3000;

export type ISendPushNotificationFormDialogProps = Pick<
  IFormDialogProps,
  'opened' | 'onClose'
> &
  ISendPushNotificationFormProps;

export const SendPushNotificationFormDialog: React.FC<
  ISendPushNotificationFormDialogProps
> = (props) => {
  const { opened, onClose, estimatedReachCount, onSubmit, ...other } = props;
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleClose = useCallback((): void => {
    setIsSubmitting(false);
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    onClose?.();
  }, [onClose]);

  const handleSubmit = useCallback(
    (values: ISendPushNotificationFormValues): Promise<void> => {
      setIsSubmitting(true);

      return Promise.resolve(onSubmit?.(values))
        .then(() => {
          setSubmitted(true);
          handleClose();
        })
        .finally(() => setIsSubmitting(false));
    },
    [onSubmit, handleClose],
  );

  return (
    <>
      <Snackbar
        open={submitted}
        onClose={() => setSubmitted(false)}
        autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION}
      >
        {t('SendPushNotificationFormDialog.snackbar.success.content', {
          count: estimatedReachCount,
        })}
      </Snackbar>

      <FormDialog
        opened={opened}
        onClose={handleClose}
        headline={
          <Stack gap={1} align="center">
            <div>{t('SendPushNotificationFormDialog.title')}</div>
            <Text dimmed>
              {t('SendPushNotificationFormDialog.subtitle', {
                count: estimatedReachCount,
              })}
            </Text>
          </Stack>
        }
        submitButtonLabel={t('common.send')}
        size="lg"
        submitting={isSubmitting}
        icon={<FontAwesomeIcon icon={faBell} />}
      >
        {({ formId }) => (
          <SendPushNotificationForm
            id={formId}
            extended
            hideActionBar
            onSubmit={handleSubmit}
            {...other}
          />
        )}
      </FormDialog>
    </>
  );
};
